import { useCallback, useEffect, useMemo, useState } from 'react'
import { ArrowLeftFromLine } from 'lucide-react'
import { type ResolvedTechField, type TechField, type TechFieldType } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { cardTitle, formCardSm, inlineInput } from '@/lib/form-styles'
import {
  FeldStatus,
  FeldStatusZeichen,
  useFeld,
  useSpeicherstelle,
  type Datensatz,
  type Feld,
  type FeldArt,
  type Speicherstelle,
} from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import {
  fetchMainMaterial,
  fetchPositionTech,
  fetchProjectTech,
  savePositionTech,
  saveProjectTech,
  type PositionTech,
  type ProjectTech,
} from '../api/project-tech.api'
import { fetchUsers } from '../api/projects-head.api'
import type { LookupUser } from '../types/project.types'
import { MitarbeiterWahl, QuelleWahl, VorschlagText, geerbtText } from './tech-eingaben'

/**
 * AP-35 Detailblatt 6 2.6 — **Das Technik-Blatt als Karten je Abschnitt.**
 *
 * Am Projekt setzt es die Vorgabe, an der Position zeigt jedes Feld, woher sein
 * Wert kommt: **„{Projekt}"** (geerbt, der Wert grau im Feld) oder **„eigen"**. Aufgelöst hat der
 * Server; die Maske rechnet nichts nach. „Zurücksetzen" schickt `null` — die
 * Position erbt dann wieder.
 *
 * Ein leeres Pflichtfeld ist rot, sperrt aber nicht: Gesperrt wird erst in der
 * Prüfliste der Leistungserklärung (Blatt 6 6. `TECH_REQUIRED_FIELD`). Deshalb
 * trägt kein Feld `pflicht` — sonst ginge ein geleertes Feld nicht mehr hinaus.
 *
 * **Projektebene (`ebene="projekt"`, Kundenwunsch 18.9.2026):** statt der
 * Abschnitte eine einzige Karte „Technik" mit nur Ausführungsklasse und
 * Bauteil-Norm. Gefunden werden die Felder über `systemKey` (Umbenennen durch
 * den Mandanten ändert nichts), hilfsweise über den Feldnamen. Die übrigen
 * Werte bleiben erhalten: Gesendet werden nur geänderte Schlüssel, der Server
 * mischt sie in den gespeicherten Stand (`pruefeWerte`).
 *
 * **Nachtrag 4 (19.9.2026):** Mitarbeiter (WPK, SAP), Statik/Planung als
 * Quelle, Text mit Vorschlägen (`tech-eingaben.tsx`). An der Position steht
 * neben der Schweißeignung der Knopf „Hauptwerkstoff aus der Stückliste".
 *
 * **AP-36 Phase F (20.9.2026):** Fall 1 — kein Knopf „Technik speichern" mehr.
 * Jedes Feld speichert beim Verlassen, Auswahl und Kästchen sofort; der
 * Werkstoff-Knopf bleibt eine Handlung mit seinem Verb und schreibt seine
 * Felder in **einem** Auftrag.
 */
export function TechFieldsCard({
  projectId,
  positionId,
  darfAendern,
  onSaved,
  ebene,
  nur,
  titel,
  ohneAbschnitte,
}: {
  projectId: string
  positionId?: string
  darfAendern: boolean
  onSaved?: () => void
  /** `"projekt"` setzt die Vorgaben der Projektebene: `nur` = EXC + Norm, `titel` = „Technik", ohne Abschnitte. */
  ebene?: 'projekt'
  /** Nur diese Felder zeigen (Feldname oder `systemKey`), in dieser Reihenfolge. */
  nur?: readonly string[]
  /** Überschrift der einen Karte, wenn `ohneAbschnitte`. */
  titel?: string
  /** Alle Felder in einer Karte statt je Abschnitt. */
  ohneAbschnitte?: boolean
}) {
  const nurFelder = nur ?? (ebene === 'projekt' ? PROJEKT_FELDER : undefined)
  const eineKarte = ohneAbschnitte ?? ebene === 'projekt'
  const kartenTitel = titel ?? 'Technik'
  const terms = useTerminology()
  const [antwort, setAntwort] = useState<TechAntwort | null>(null)
  const [fehler, setFehler] = useState<string | null>(null)
  const [benutzer, setBenutzer] = useState<LookupUser[]>([])
  const [werkstoffInfo, setWerkstoffInfo] = useState<string | null>(null)
  const schema = antwort?.schema ?? null
  const aufgeloest: Record<string, ResolvedTechField> = antwort && 'fields' in antwort ? antwort.fields : OHNE_VERERBUNG
  const mitMitarbeitern = !!schema?.fields.some((f) => f.type === 'user' && !f.hidden)

  useEffect(() => {
    if (!mitMitarbeitern) return
    fetchUsers()
      .then(setBenutzer)
      .catch(() => setBenutzer([]))
  }, [mitMitarbeitern])

  const laden = useCallback(async () => {
    try {
      setAntwort(positionId ? await fetchPositionTech(projectId, positionId) : await fetchProjectTech(projectId))
      setFehler(null)
    } catch (e) {
      setFehler(apiErrorText(e, 'Das Technik-Blatt konnte nicht geladen werden.'))
    }
  }, [projectId, positionId])

  useEffect(() => {
    void laden()
  }, [laden])

  /**
   * **Eine Stelle je Träger** (AP-36 7.2) — und bewusst **nicht** `project:<id>`
   * bzw. `position:<id>`: Der Satz ist zwar derselbe, der Schreibweg aber ein
   * anderer Endpunkt (`PUT …/tech` mit `{ values }`). Eine zweite Stelle unter
   * dem Schlüssel des Reiters „Daten" überschriebe über `setzeOptionen` dessen
   * `schreibe` — die Projektfelder gingen dann an das Technik-`PUT`.
   *
   * Ohne `keepalive`: Der Endpunkt nimmt den Patch nicht flach, sondern in
   * `{ values }` verpackt; beim Schließen des Tabs warnt darum der Browser,
   * statt einen Aufruf zu schicken, den der Server abweisen müsste.
   */
  const stelle = useSpeicherstelle<TechAntwort>({
    schluessel: positionId ? `techPosition:${positionId}` : `techProjekt:${projectId}`,
    bezeichnung: kartenTitel,
    daten: antwort,
    werte: techWerte,
    schreibe: async (patch, stand) => {
      const neu = positionId
        ? await savePositionTech(projectId, positionId, patch, stand)
        : await saveProjectTech(projectId, patch, stand)
      // Die Antwort trägt die neu aufgelösten Felder — ohne sie zeigten die
      // Schilder „eigen"/„{Projekt}" und die grauen Vorgaben den alten Stand.
      setAntwort(neu)
      onSaved?.()
      return neu
    },
  }) as Speicherstelle<unknown>

  const nurSchluessel = nurFelder?.join('|')
  const abschnitte = useMemo(() => {
    const sichtbar = (schema?.fields ?? []).filter((f) => !f.hidden)
    let felder = sichtbar
    if (nurSchluessel !== undefined) {
      const gesucht = nurSchluessel.split('|')
      const rang = (f: TechField) => {
        const i = f.systemKey ? gesucht.indexOf(f.systemKey) : -1
        return i >= 0 ? i : gesucht.indexOf(f.name)
      }
      felder = sichtbar.filter((f) => rang(f) >= 0).sort((a, b) => rang(a) - rang(b))
    }
    if (eineKarte) return felder.length ? ([[kartenTitel, felder]] as [string, TechField[]][]) : []
    const aus = new Map<string, TechField[]>()
    for (const f of felder) {
      const s = f.section || 'Allgemein'
      aus.set(s, [...(aus.get(s) ?? []), f])
    }
    return [...aus.entries()]
  }, [schema, nurSchluessel, eineKarte, kartenTitel])

  /**
   * Nachtrag 4 — Hauptwerkstoff der Stückliste. Eine Handlung mit ihrem Verb,
   * die mehrere Felder betrifft: Sie gehen in **einem** Auftrag hinaus (8.3),
   * damit Sorte und die davon abhängigen Felder nicht halb ankommen.
   */
  async function ausStueckliste() {
    if (!positionId || !schema) return
    setWerkstoffInfo(null)
    setFehler(null)
    try {
      const r = await fetchMainMaterial(projectId, positionId)
      if (!r.werkstoff) {
        setWerkstoffInfo(
          r.zeilen === 0
            ? 'Die Stückliste ist leer.'
            : `In der Stückliste ist kein bekannter Werkstoff eingetragen${r.unbekannt.length ? ` (gefunden: ${r.unbekannt.slice(0, 5).join(', ')})` : ''}.`,
        )
        return
      }
      const patch: Datensatz = {}
      const labels: Record<string, string> = {}
      const gesetzt: string[] = []
      for (const [dop, wert] of Object.entries(r.vorschlag)) {
        const f = schema.fields.find((x) => !x.hidden && x.dopField === dop) ?? schema.fields.find((x) => !x.hidden && x.name === dop)
        if (!f || !wert) continue
        if (f.type === 'enum' && !(f.options ?? []).includes(wert)) continue
        const eigen = stelle.daten[f.name]
        const wirksam = eigen !== null && eigen !== undefined && eigen !== '' ? eigen : aufgeloest[f.name]?.effective
        if (wirksam === wert) continue
        patch[f.name] = wert
        labels[f.name] = f.label
        gesetzt.push(f.label)
      }
      const anteil = Math.round(r.anteil * 100)
      const woher = r.basis === 'weight' ? `${anteil} % des Gewichts` : `${r.treffer} von ${r.zeilen} Zeilen`
      if (gesetzt.length) {
        const ergebnis = await stelle.sende({ kennung: 'hauptwerkstoff', felder: Object.keys(patch), labels, patch })
        if (ergebnis.art === 'fehler') {
          setFehler(ergebnis.text)
          return
        }
        if (ergebnis.art === 'verworfen') {
          setWerkstoffInfo(`Hauptwerkstoff ${r.werkstoff.sorte} (${woher}). Nichts übernommen.`)
          return
        }
      }
      setWerkstoffInfo(
        `Hauptwerkstoff ${r.werkstoff.sorte} (${woher}). ` +
          (gesetzt.length ? `Übernommen: ${gesetzt.join(', ')}.` : 'Die Felder stimmen schon.'),
      )
    } catch (e) {
      setFehler(apiErrorText(e, 'Die Stückliste konnte nicht gelesen werden.'))
    }
  }

  if (!schema) return fehler ? <p className="text-sm text-destructive">{fehler}</p> : null
  if (abschnitte.length === 0) {
    return (
      <div className={formCardSm}>
        <h3 className={cardTitle}>Technik</h3>
        <p className="mt-2 text-sm text-muted-foreground">
          Kein Technik-Blatt eingerichtet — in den Einstellungen unter „Technik &amp; CE“ eine Vorlage wählen.
        </p>
      </div>
    )
  }

  return (
    <div className="space-y-3">
      {abschnitte.map(([abschnitt, felder]) => (
        <div key={abschnitt} className={formCardSm}>
          <h3 className={cardTitle}>
            {abschnitt}
            {!positionId && <span className="ml-2 text-xs font-normal text-muted-foreground">Vorgabe für alle {terms.positionPlural}</span>}
          </h3>
          <div className="mt-3 grid gap-x-4 gap-y-2 sm:grid-cols-2">
            {felder.map((f) => (
              <TechnikFeld
                key={f.name}
                schemaFeld={f}
                stelle={stelle}
                aufgeloest={aufgeloest[f.name]}
                anPosition={!!positionId}
                darfAendern={darfAendern}
                begriffProjekt={terms.project}
                benutzer={benutzer}
                werkstoffInfo={werkstoffInfo}
                onWerkstoff={ausStueckliste}
              />
            ))}
          </div>
        </div>
      ))}
      {fehler && <p className="text-sm text-destructive">{fehler}</p>}
    </div>
  )
}

/** Projektebene: nur Ausführungsklasse und Bauteil-Norm (`systemKey` der EN-1090-Vorlage). */
const PROJEKT_FELDER: readonly string[] = ['executionClass', 'standard']

/** Die Antwort, aus der die Karte lebt — am Projekt die Werte, an der Position die aufgelösten Felder. */
type TechAntwort = ProjectTech | PositionTech

/** Am Projekt gibt es nichts zu erben; eine feste Kennung spart einen neuen Verweis je Render. */
const OHNE_VERERBUNG: Record<string, ResolvedTechField> = {}

/**
 * Der Datensatz der Stelle: je Feld des Schemas ein **flacher** Schlüssel, dazu
 * der Stand. Jeder Name steht ausdrücklich da, weil die Stelle Antworten
 * zusammenführt statt sie zu ersetzen — ein am Server geleertes Feld muss als
 * `null` ankommen, sonst bliebe der alte Wert die Basis.
 *
 * An der Position sind es die **eigenen** Werte (`fields[name].own`): Nur so
 * geht ein geleertes Feld wieder als `null` hinaus („zurück auf Projekt"). Der
 * geerbte Wert ist reine Anzeige und gehört deshalb nicht in die Basis.
 */
function techWerte(antwort: TechAntwort): Datensatz {
  const eigen = (name: string) => ('fields' in antwort ? (antwort.fields[name]?.own ?? null) : (antwort.values[name] ?? null))
  const werte: Datensatz = { updatedAt: antwort.updatedAt }
  for (const f of antwort.schema.fields) werte[f.name] = eigen(f.name)
  return werte
}

/** Art des Bausteins je Feldtyp; alles Übrige ist Text. */
const ART: Partial<Record<TechFieldType, FeldArt>> = { enum: 'auswahl', boolean: 'schalter', number: 'zahl', date: 'datum' }

/** Objektwerte (`user`, `source`) reisen als JSON durch die Texteingabe des Bausteins. */
const OBJEKTWERT: readonly TechFieldType[] = ['user', 'source']

/** Ein leeres Auswahlfeld, das einen geerbten Wert zeigt, steht grau wie ein Platzhalter. */
export const grauWennGeerbt = (aktuell: string, geerbt: string | undefined) => (!aktuell && geerbt ? 'text-muted-foreground' : '')

/**
 * Ein Feld des Schemas mit **genau einem** `useFeld`. Eine eigene Komponente je
 * Feld, weil die Liste der Felder aus dem Schema kommt und sich ändert — Hooks
 * laufen nicht in einer Schleife über eine wechselnde Liste.
 */
function TechnikFeld({
  schemaFeld,
  stelle,
  aufgeloest,
  anPosition,
  darfAendern,
  begriffProjekt,
  benutzer,
  werkstoffInfo,
  onWerkstoff,
}: {
  schemaFeld: TechField
  stelle: Speicherstelle<unknown>
  aufgeloest?: ResolvedTechField
  anPosition: boolean
  darfAendern: boolean
  begriffProjekt: string
  benutzer: LookupUser[]
  werkstoffInfo: string | null
  onWerkstoff: () => Promise<void>
}) {
  const f = schemaFeld
  const feld = useFeld<unknown>(stelle, f.name, {
    label: f.label,
    art: ART[f.type] ?? 'text',
    gesperrt: !darfAendern,
    // Kein `pflicht`: Ein leeres Pflichtfeld ist hier rot, sperrt aber nicht
    // (Blatt 6 6.) — und an der Position muss „zurücksetzen" leer senden dürfen.
    ...(f.type === 'boolean' ? { lies: liesJaNein, schreib: schreibJaNein } : {}),
    ...(f.type === 'number' ? { schreib: schreibZahl } : {}),
    ...(OBJEKTWERT.includes(f.type) ? { lies: liesObjekt, schreib: schreibObjekt } : {}),
  })

  const eigen = feld.wert
  const hatEigen = eigen !== null && eigen !== undefined && eigen !== ''
  const wirksam = anPosition ? (hatEigen ? eigen : aufgeloest?.effective) : eigen
  const leer = wirksam === null || wirksam === undefined || wirksam === ''
  const breit = f.type === 'source' || (f.type === 'string' && f.multiline)
  const werkstoffKnopf = anPosition && darfAendern && f.dopField === 'weldability'

  return (
    <div className={`block text-sm ${breit ? 'sm:col-span-2' : ''}`}>
      <span className={`flex items-center gap-2 text-xs ${f.required && leer ? 'text-destructive' : 'text-muted-foreground'}`}>
        {f.label}
        {f.required && ' *'}
        {anPosition && (
          <span className={`rounded px-1.5 py-px text-[10px] font-medium ${hatEigen ? 'bg-primary/15 text-primary' : 'bg-muted text-muted-foreground'}`}>
            {hatEigen ? 'eigen' : begriffProjekt}
          </span>
        )}
        {anPosition && hatEigen && darfAendern && (
          <button type="button" className="text-[10px] underline" onClick={() => feld.setze(null)}>
            zurücksetzen
          </button>
        )}
        <FeldStatusZeichen status={feld} />
      </span>
      <div className={werkstoffKnopf ? 'flex items-start gap-1' : undefined}>
        <div className="min-w-0 flex-1">
          <Eingabe
            schemaFeld={f}
            feld={feld}
            geerbt={anPosition && !hatEigen ? aufgeloest?.effective : undefined}
            deaktiviert={!darfAendern}
            begriffProjekt={begriffProjekt}
            benutzer={benutzer}
          />
        </div>
        {werkstoffKnopf && (
          <button
            type="button"
            className="mt-1 rounded p-1 text-muted-foreground hover:bg-muted hover:text-foreground"
            title="Hauptwerkstoff aus der Stückliste ermitteln und die davon abhängigen Felder ausfüllen"
            aria-label="Hauptwerkstoff aus der Stückliste"
            onClick={() => void onWerkstoff()}
          >
            <ArrowLeftFromLine size={16} />
          </button>
        )}
      </div>
      <FeldStatus status={feld} zeichen={false} />
      {werkstoffKnopf && werkstoffInfo && <span className="mt-0.5 block text-[11px] text-primary">{werkstoffInfo}</span>}
      {f.help && <span className="mt-0.5 block text-[11px] text-muted-foreground">{f.help}</span>}
    </div>
  )
}

/**
 * Der Schalter steht hier als dreiwertige Auswahl (— / ja / nein): Leer heißt
 * „erbt", nicht „nein" — deshalb nicht die Vorgabe der Art `schalter`.
 */
const liesJaNein = (t: string): unknown => (t === '' ? null : t === 'true')
const schreibJaNein = (w: unknown): string => (w === null || w === undefined ? '' : String(w))

/**
 * Die Zahl steht in einem `<input type="number">`; dessen Wert kennt nur die
 * Punkt-Schreibweise. Die Vorgabe der Art `zahl` formatiert nach Sprache und
 * ergäbe „1,5" — das Feld zeigte dann gar nichts.
 */
const schreibZahl = (w: unknown): string => (w === null || w === undefined ? '' : String(w))

/**
 * `user` und `source` tragen Objekte. Der Baustein führt jede Eingabe als Text,
 * also reisen sie als JSON hin und zurück — `setze(objekt)` kommt so unverändert
 * im Patch an.
 */
const schreibObjekt = (w: unknown): string => (w === null || w === undefined ? '' : JSON.stringify(w))
const liesObjekt = (t: string): unknown => {
  if (t.trim() === '') return null
  try {
    return JSON.parse(t)
  } catch {
    return null
  }
}

/** Ein Feld nach Typ. Geerbte Werte stehen grau als Platzhalter, nicht als Wert. */
function Eingabe({
  schemaFeld,
  feld,
  geerbt,
  deaktiviert,
  begriffProjekt,
  benutzer,
}: {
  schemaFeld: TechField
  feld: Feld<unknown>
  geerbt: unknown
  deaktiviert: boolean
  begriffProjekt: string
  benutzer: LookupUser[]
}) {
  const geerbtAls = geerbt !== undefined && geerbt !== null ? geerbtText(geerbt, begriffProjekt) : undefined
  const leerText = geerbtAls ?? '—'
  if (schemaFeld.type === 'user') {
    return (
      <MitarbeiterWahl
        feld={schemaFeld}
        wert={feld.wert}
        geerbt={geerbtAls}
        benutzer={benutzer}
        deaktiviert={deaktiviert}
        onChange={(v) => feld.setze(v)}
      />
    )
  }
  if (schemaFeld.type === 'source') {
    return <QuelleWahl wert={feld.wert} geerbt={geerbtAls} begriffProjekt={begriffProjekt} deaktiviert={deaktiviert} onChange={(v) => feld.setze(v)} />
  }
  if (schemaFeld.type === 'string' && (schemaFeld.options?.length || schemaFeld.multiline)) {
    // Tippen füllt nur die Eingabe (`tippe`), gespeichert wird beim Verlassen;
    // ein Vorschlag aus der Liste ist eine Auswahl und speichert sofort (3.2).
    // Angezeigt wird der **Text** der Eingabe, nicht der gelesene Wert — sonst
    // verschwände ein eben getipptes Leerzeichen wieder aus dem Feld.
    return (
      <VorschlagText
        feld={schemaFeld}
        wert={feld.props.value}
        platzhalter={geerbtAls}
        deaktiviert={deaktiviert}
        onChange={feld.tippe}
        onBlur={feld.props.onBlur}
        onWahl={(v) => feld.setze(v)}
      />
    )
  }
  if (schemaFeld.type === 'boolean' || schemaFeld.type === 'enum') {
    const aktuell = feld.props.value
    const liste = schemaFeld.options ?? []
    // Ein gespeicherter Wert, der nicht mehr in der Auswahl steht (Vorlage geändert), bleibt sichtbar.
    const optionen =
      schemaFeld.type === 'boolean'
        ? [['true', 'ja'], ['false', 'nein']]
        : [...(aktuell && !liste.includes(aktuell) ? [aktuell] : []), ...liste].map((o) => [o, o])
    return (
      <select {...feld.props} className={`${inlineInput} ${grauWennGeerbt(aktuell, geerbtAls)}`}>
        <option value="">{leerText}</option>
        {optionen.map(([v, l]) => (
          <option key={v} value={v} className="text-foreground">
            {l}
          </option>
        ))}
      </select>
    )
  }
  return (
    <input
      {...feld.props}
      className={inlineInput}
      type={schemaFeld.type === 'number' ? 'number' : schemaFeld.type === 'date' ? 'date' : 'text'}
      placeholder={geerbt !== undefined && geerbt !== null ? leerText : undefined}
    />
  )
}
